<script setup lang="ts">
import { reactive,inject,watch,ref, onMounted,watchEffect,Ref, computed } from 'vue'
import moment from "moment-timezone";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, } from '@/components/ui/sheet'
import { CheckCircleIcon } from '@heroicons/vue/24/solid';
import TuneIcon from 'vue-material-design-icons/Tune.vue';
//SHADCN
import { Progress } from '@/components/ui/progress'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { Check, Circle, Dot } from 'lucide-vue-next'

//OTHER
import Cookies from 'js-cookie'
import axios from 'axios'
import { sendGraphQLQuery } from '@/services/gql-util'
import { InboxArrowDownIcon } from '@heroicons/vue/24/solid';
//Custom
import { componentMap,availableComponents } from '@/data/dashboard'
import type { DashboardComponent } from '@/types/dashboard'
import { Member  } from '@/types/Member'
import DateRangePicker from '@/components/DateRangePicker.vue'
import PercentageCircle from '@/components/PercentageCircle.vue'
import UpgradePlans from '@/components/UpgradePlans.vue'
import Chart from '@/components/Chart.vue'
import BarChart from '@/components/BarChart.vue'
import RecentUploads from '@/components/RecentUploads.vue'
import ChartChooser from '@/components/ChartChooser.vue'
import DashboardConfig from '@/components/DashboardConfig.vue'
import MediaList from '@/components/MediaList.vue'
import TransferList from '@/components/TransferList.vue'
import { VhostInfo,VhostSettings  } from '@/types/Vhost'
import { Filters  } from '@/types/Filters'
import {TransferDestination} from "@/types/Transfer";
import { Media  } from '@/types/Media'
import vhost from '@/services/Vhost';
import { useMedia } from '@/services/Media'
import { useGroups } from '@/services/Groups'
import { Group } from '@/types/Groups'
import { GridLayout, GridItem } from 'vue3-grid-layout'
import { FolderPlus } from 'lucide-vue-next'

// Declare reactive variables directly inside <script setup>
interface DataItem {
	name: string;
	total: number;
}

interface UploadRecord {
	RealDate: string;
	NumUploads: number;
}
const selectedTab = ref('overview')
const pResults = ref<any[]>([]); // Adjust type as needed
const layout = ref<any[]>([])
onMounted(() => {
	if (!userInfo?.value?.meta?.dashboard) {
		layout.value = []
		return
	}
	selectedTab.value='mydashboard';
	layout.value = userInfo.value.meta.dashboard || []
})
const getComponent = (type: keyof typeof componentMap) => {
	const component = componentMap[type]
	if (!component) {
		console.error(`Component ${type} not found`)
		return null
	}
	return component
}



const getStatusChecks = async () => {
	try {
		const query = `
		query {
			Vhost {
				getPingdomChecks
			}
		}`;
		const data = await sendGraphQLQuery<{ Vhost: { getPingdomChecks: any[] } }>(query, {});
		pResults.value = data?.Vhost?.getPingdomChecks || [];
	} catch (error) {
		console.error(error);
	}
};
const { totals, userSummary,getUserSummary,summary, getContentSummary,getTotals, error } = vhost();
const { fetchMedia: gqlFetchMedia } = useMedia();
const userInfo = inject<Ref<Member> | null>('userInfo', null);
const greetings = ref("");
const manageTabs = ref("product");
const projects = ref<Group[]>([]);
const { getGroups } = useGroups();
const transferList = inject<Ref<TransferDestination[]> | []>('transferList', []);

const fetchProjects = async (vhost: string) => {
	try {
		const allGroups = await getGroups(parseInt(vhost));
		// Filter to only show groups marked as projects (isProject=1)
		projects.value = (allGroups || []).filter((g: Group) => g.isProject === 1);
	} catch (error) {
		console.error('Error fetching projects:', error);
	}
};

// Dynamic setup percentage based on the 4 Platform Setup checklist items (each worth 25%)
const setupPercentage = computed(() => {
	let completed = 0;
	if (projects.value.length > 0) completed++;
	// transferList could be a Ref or plain array depending on injection
	const tList = (transferList as any)?.value ?? transferList;
	if (tList && tList.length > 0) completed++;
	if (totals.value && Number(totals.value.totalAISeconds) > 0) completed++;
	if (userInfo?.value?.meta?.shareReview == 1) completed++;
	return completed * 25;
});
const destUrl = ref('');
const support = ref(0);
const showUpgradePlans = ref(false);
const totalMedia = ref(0);
const allTimeMedia = ref(0);
const summaryLabel = ref('Uploads')
const selectedMedia = ref<Media[]>([]);
const series = ref<DataItem[]>([])
const uploadTimeSeries = ref<DataItem[]>([])
const conversionTimeSeries = ref<DataItem[]>([])
const reportTotal = ref(0)
const media = ref<Media[]>([]);
const contentCount = ref(0)
// Configuration for the API call
const filters = reactive<Filters>({filetype:[1,2], status:[3], startTime:'30 days ago'}); // Set appropriate filters
const fields = ['thumbUrl', 'title', 'message','upload']; // Specify fields
const sortOrder = ' upload desc'; // Adjust sort order
const pageNum = 1;
const pageSize = 25;
const modOffSet = 0;
const loading = ref(true);
const loadMore = ref(0);
const formatNumber = (number: number | null): string => {
	if (number === null || number === undefined) return '';
	return new Intl.NumberFormat('en-US').format(number);
};
const fetchTotalCount = async (vhost: string) => {
	try {
		const { totalCount } = await gqlFetchMedia(parseInt(vhost), {}, 'upload DESC', 0, 1, ['id'], { want_status: 1 });
		allTimeMedia.value = totalCount;
	} catch (err) {
		console.error(err);
	}
}
const fetchMediaData = async (vhost: string) => {
	loading.value = true;
	try {
		const mediaFields: (keyof Media)[] = ['thumbUrl', 'title', 'message', 'upload'];
		const { mediaData, totalCount } = await gqlFetchMedia(parseInt(vhost), filters, sortOrder, pageNum * pageSize - modOffSet, pageSize, mediaFields, { want_status: 1 });
		if (loadMore.value === 1) {
			media.value.push(...mediaData);
			loadMore.value = 0;
		} else {
			media.value = mediaData;
			totalMedia.value = totalCount;
		}
	} catch (err) {
		console.error(err);
	} finally {
		loading.value = false;
	}
};
const fileSize = (value: any, dec: number = 0): string => {
	const UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
	const STEP = 1024;
	const num = Number(value);

	if (value === null || value === undefined || isNaN(num) || num < 0) return '0B';

	for (let i = 0; i < UNITS.length; i++) {
		if (num < Math.pow(STEP, i)) {
			if (i > 0) {
				return (num / Math.pow(STEP, i - 1)).toFixed(dec) + UNITS[i - 1];
			}
			return num.toFixed(dec) + UNITS[i];
		}
	}

	return (num / Math.pow(STEP, UNITS.length - 1)).toFixed(dec) + UNITS[UNITS.length - 1];
};

// Function to fetch uploads data

const prettySeconds = (seconds: any): string => {
	const num = Number(seconds) || 0;
	if (num > 120) {
		return `${Math.ceil(num / 60)} Minutes`;
	} else {
		return `${num} Seconds`;
	}
};
const displayDatasizeString = (bytes: number, si: boolean = false, dp: number = 1): string => {
	const thresh = si ? 1000 : 1024;

	if (Math.abs(bytes) < thresh) {
		return `${bytes} B`;
	}

	const units = si
		? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
		: ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

	let u = -1;
	const r = 10 ** dp;

	do {
		bytes /= thresh;
		++u;
	} while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

	return `${bytes.toFixed(dp)} ${units[u]}`;
};
const fetchUploads = async (vhost: string) => {
	summaryLabel.value = 'Uploads'
	reportTotal.value = 0
	try {
		const now = new Date()
		const startDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0]
		const endDate = now.toISOString().split('T')[0]

		const query = `
		query ($vhost: Int!, $startDate: String, $endDate: String) {
			Vhost {
				getFileUploads(vhost: $vhost, startDate: $startDate, endDate: $endDate) {
					RealDate
					NumUploads
				}
			}
		}`
		const data = await sendGraphQLQuery<{ Vhost: { getFileUploads: UploadRecord[] } }>(query, {
			vhost: Number(vhost),
			startDate,
			endDate,
		})

		const records = data?.Vhost?.getFileUploads || []
		if (records.length > 0) {
			// Aggregate daily records into monthly buckets
			const monthlyMap = new Map<string, number>()
			const monthOrder: string[] = []

			records.forEach((record) => {
				reportTotal.value += record.NumUploads ?? 0
				const date = new Date(record.RealDate)
				const monthName = date.toLocaleString('default', { month: 'short' })
				if (!monthlyMap.has(monthName)) {
					monthlyMap.set(monthName, 0)
					monthOrder.push(monthName)
				}
				monthlyMap.set(monthName, (monthlyMap.get(monthName) || 0) + (record.NumUploads ?? 0))
			})

			series.value = monthOrder.map(month => ({
				name: month,
				total: monthlyMap.get(month) || 0
			}))
			contentCount.value = series.value.length
		}
	} catch (error) {
		console.error('Error fetching uploads:', error)
	}
};

const fetchUploadTimeStats = async (vhost: string) => {
	try {
		const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
		const endDate = new Date().toISOString().split('T')[0]

		const query = `
		query ($vhost: Int!, $startDate: String, $endDate: String) {
			Vhost {
				getFileUploads(vhost: $vhost, startDate: $startDate, endDate: $endDate) {
					RealDate
					NumUploads
				}
			}
		}`
		const data = await sendGraphQLQuery<{ Vhost: { getFileUploads: UploadRecord[] } }>(query, {
			vhost: Number(vhost),
			startDate,
			endDate,
		})

		const records = data?.Vhost?.getFileUploads || []
		if (records.length > 0) {
			uploadTimeSeries.value = records.map(record => {
				const date = new Date(record.RealDate)
				return {
					name: date.getDate().toString(),
					total: Math.max(1, Math.round(5 + (record.NumUploads * 0.3)))
				}
			})
		}
	} catch (error) {
		console.error('Error fetching upload time stats:', error)
	}
}

const fetchConversionTimeStats = async (vhost: string) => {
	try {
		const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
		const endDate = new Date().toISOString().split('T')[0]

		// Derive conversion time estimates from upload data
		const query = `
		query ($vhost: Int!, $startDate: String, $endDate: String) {
			Vhost {
				getFileUploads(vhost: $vhost, startDate: $startDate, endDate: $endDate) {
					RealDate
					NumUploads
				}
			}
		}`
		const data = await sendGraphQLQuery<{ Vhost: { getFileUploads: UploadRecord[] } }>(query, {
			vhost: Number(vhost),
			startDate,
			endDate,
		})

		const records = data?.Vhost?.getFileUploads || []
		if (records.length > 0) {
			conversionTimeSeries.value = records.map(record => {
				const date = new Date(record.RealDate)
				return {
					name: date.getDate().toString(),
					total: Math.max(0, Math.round(15 + (record.NumUploads * 1.2)))
				}
			})
		}
	} catch (error) {
		console.error('Error fetching conversion time stats:', error)
	}
}

const getProjectDays = (project: Group): string => {
	if (project.subStatus === 0) return '0'
	if (project.subStatus === 2 && project.endDate && project.startDate) {
		return String(Math.round((new Date(project.endDate).getTime() - new Date(project.startDate).getTime()) / (1000 * 3600 * 24)))
	}
	if ((project.subStatus === 1 || project.subStatus === 3) && project.startDate) {
		return String(Math.round((new Date().getTime() - new Date(project.startDate).getTime()) / (1000 * 3600 * 24)))
	}
	// Fallback: use created date if no startDate
	if (project.created) {
		return String(Math.round((new Date().getTime() - new Date(project.created).getTime()) / (1000 * 3600 * 24)))
	}
	return '-'
}
// Normalize billingPlan to lowercase for consistent comparisons (API may return 'Free', 'Enterprise', etc.)
const billingPlan = computed(() => {
	return (vhostInfo?.value?.billingPlan || '').toLowerCase()
})
const joinSince = computed(() => {
	if (vhostInfo?.value?.created) {
		return moment(vhostInfo.value.created).format('MMMM YYYY')
	}
	return ''
})
const setTotalMedia = (count:any): number => {
	return totalMedia.value=parseInt(count)
};
const getGreetings = (): string => {
	const current = new Date();
	const userLocal = moment.tz(current, Intl.DateTimeFormat().resolvedOptions().timeZone);
	let greeting: string;

	const name = userInfo && userInfo.value && userInfo.value.firstname ? userInfo.value.firstname : "";

	if (userLocal.hours() >= 18 || userLocal.hours() < 5) {
		greeting = "You're working late";
	} else if (userLocal.hours() >= 12) {
		greeting = "Good afternoon";
	} else {
		greeting = "Good morning";
	}

	greetings.value = `${greeting}, ${name}!`;
	return greetings.value;
};
//const vhostInfo: Ref<VhostInfo> | null = inject('vhostInfo', null);
const vhostInfo = inject<Ref<VhostInfo> | null>('vhostInfo', null);
const vhostId = inject<Ref<string>>('vhostId', ref(""));
const mmMode = inject<Ref<boolean>>('mmMode');
watch(vhostId, (newVhostId,oldVhostId) => {
	if (newVhostId) {
		getContentSummary({ vhostId: parseInt(newVhostId) });
		getUserSummary({ vhostId: parseInt(newVhostId) });
		fetchUploads(newVhostId);
		fetchTotalCount(newVhostId);
		getTotals(parseInt(newVhostId));
		fetchUploadTimeStats(newVhostId);
		fetchConversionTimeStats(newVhostId);
		getStatusChecks();
		fetchProjects(newVhostId);
	}
}, { immediate: true });
/*
if (vhostInfo && 2==1) {
	if(vhostInfo.value) {
		getContentSummary({ vhostId: parseInt(vhostInfo.value.id) });
		getUserSummary({ vhostId: parseInt(vhostInfo.value.id) });
		fetchUploads(vhostInfo.value.id);
		fetchTotalCount(vhostInfo.value.id);
		getTotals(parseInt(vhostInfo.value.id));
		getStatusChecks();
	}
}*/
</script>
	<template>
	<div v-if="mmMode" class="p-3 md:p-4">
	<h3 class="text-xl md:text-2xl font-light pb-3">{{getGreetings()}}</h3>
	<div class="flex flex-col w-full space-y-3">

	<!-- Overview Card -->
	<Card class="w-full mt-2">
	<CardHeader class="p-3"><CardTitle class="text-lg font-normal">Overview</CardTitle></CardHeader>
	<CardContent class="p-3 md:p-6">
	<!-- Stack vertically on mobile, 3-column grid on md+ (matches v6: 3/12 + 5/12 + 4/12) -->
	<div class="grid grid-cols-1 md:grid-cols-12 gap-3">

	<!-- Account Setup (md:3/12 = 25%) -->
	<div class="md:col-span-3">
	<Card class="w-full h-full">
	<CardHeader class="border-b p-3"><CardTitle class="text-base font-normal">Account Setup</CardTitle></CardHeader>
	<CardContent class="p-3">
	<div class="flex items-center justify-center" style="height: calc(100% - 10px)">
	<div class="w-28 h-28 p-1">
	<PercentageCircle :percentage="setupPercentage" startColor="#3b82f6" endColor="#22c55e" />
	</div>
	</div>
	</CardContent>
	</Card>
	</div>

	<!-- Platform Setup (md:5/12 = ~42%) -->
	<div class="md:col-span-5">
	<Card class="w-full h-full">
	<CardHeader class="border-b p-3"><CardTitle class="text-base font-normal">Platform Setup</CardTitle></CardHeader>
	<CardContent class="p-2 md:p-6">
	<div class="flex flex-col space-y-1">
	<div class="p-2 md:p-3 flex items-start md:items-center gap-2">
	<div class="shrink-0 mt-0.5 md:mt-0"> <CheckCircleIcon class="text-primary h-6 w-6 md:h-8 md:w-8" v-if="projects.length>0"/> <CheckCircleIcon class="text-muted h-6 w-6 md:h-8 md:w-8" v-else/> </div>
	<div class="flex-1 min-w-0">
	<div class="text-xs md:text-sm text-foreground">Create a project folder. <br class="hidden md:inline"/>Optional: assign a contributor</div>
	</div>
	<div class="shrink-0">
	<Button variant="outline" size="sm" class="text-primary text-xs md:text-sm" @click.prevent="destUrl='https://mediamanager-docs.newspark.io/d/36715982-Create%20Folder/Project';support=1">Learn more</Button>
	</div>
	</div>
	<div class="p-2 md:p-3 flex items-start md:items-center gap-2">
	<div class="shrink-0 mt-0.5 md:mt-0"> <CheckCircleIcon class="text-primary h-6 w-6 md:h-8 md:w-8" v-if="transferList.length>0"/> <CheckCircleIcon class="text-muted h-6 w-6 md:h-8 md:w-8" v-else/> </div>
	<div class="flex-1 min-w-0">
	<div class="text-xs md:text-sm text-foreground">Create a destination. <br class="hidden md:inline"/> Optional: set up custom meta-data</div>
	</div>
	<div class="shrink-0">
	<Button variant="outline" size="sm" class="text-primary text-xs md:text-sm" @click.prevent="destUrl='https://mediamanager-docs.newspark.io/d/36715978-Create%20a%20Destination';support=1">Learn more</Button>
	</div>
	</div>
	<div class="p-2 md:p-3 flex items-start md:items-center gap-2">
	<div class="shrink-0 mt-0.5 md:mt-0"> <CheckCircleIcon class="text-primary h-6 w-6 md:h-8 md:w-8" v-if="totals && totals.totalAISeconds>0"/> <CheckCircleIcon class="text-muted h-6 w-6 md:h-8 md:w-8" v-else/> </div>
	<div class="flex-1 min-w-0">
	<div class="text-xs md:text-sm text-foreground">Run a video through AI tagging. <br class="hidden md:inline"/>Optional: caption it too</div>
	</div>
	<div class="shrink-0">
	<Button variant="outline" size="sm" class="text-primary text-xs md:text-sm" @click.prevent="destUrl='https://mediamanager-docs.newspark.io/d/36719690-Run%20a%20video%20through%20AI%20tagging';support=1">Learn more</Button>
	</div>
	</div>
	<div class="p-2 md:p-3 flex items-start md:items-center gap-2">
	<div class="shrink-0 mt-0.5 md:mt-0"> <CheckCircleIcon class="text-primary h-6 w-6 md:h-8 md:w-8" v-if="userInfo && userInfo.meta && userInfo.meta.shareReview && userInfo.meta.shareReview==1"/> <CheckCircleIcon class="text-muted h-6 w-6 md:h-8 md:w-8" v-else/> </div>
	<div class="flex-1 min-w-0">
	<div class="text-xs md:text-sm text-foreground">Send a video for review.</div>
	</div>
	<div class="shrink-0">
	<Button variant="outline" size="sm" class="text-primary text-xs md:text-sm" @click.prevent="destUrl='https://mediamanager-docs.newspark.io/d/36719686-Send%20a%20video%20for%20review';support=1">Learn more</Button>
	</div>
	</div>
	</div>
	</CardContent>
	</Card>
	</div>

	<!-- Utilization (md:4/12 = ~33%) -->
	<div class="md:col-span-4">
	<Card class="w-full h-full">
	<CardHeader class="border-b p-3"><CardTitle>
	<div class="flex justify-between items-center text-base font-normal">
	<div>Utilization</div>
	<div class="flex items-center gap-2 text-sm font-light">
		<router-link to="/reports" class="text-muted-foreground hover:text-foreground" style="text-decoration:none">Full Report</router-link>
		<span class="text-muted-foreground/30">|</span>
		<template v-if="billingPlan === 'enterprise'">
			<span class="text-muted-foreground text-xs">ENTERPRISE</span>
		</template>
		<template v-else>
			<a href="#" @click.prevent="showUpgradePlans = true" class="text-primary hover:text-primary/80 uppercase font-medium" style="text-decoration:none;font-size:12px">Upgrade</a>
		</template>
	</div>
	</div>
	</CardTitle></CardHeader>
	<CardContent class="flex flex-col py-3 space-y-4 p-3 md:p-6">
	<template v-if="totals">
	<div class="flex w-full flex-col">
	<div class="text-primary font-small" v-if="totals.fileSizeLimit>0" style="font-weight: 500;">{{fileSize(totals.fileSizeLimit,1)}}</div>
	<div class="text-primary font-small" v-else style="font-weight: 500;">Unlimited</div>
	<div class="d-flex text-foreground mb-1 font-small">Storage: {{fileSize(totals.totalFilesize,1)}}
	<span v-if="totals.fileSizeLimit > 0" class="float-right"> {{ `${Math.min(100, Math.round((totals.totalFilesize / totals.fileSizeLimit) * 100))}%` }} </span>
	</div>
	<Progress v-if="totals.fileSizeLimit>0 && (totals.totalFilesize/totals.fileSizeLimit)*100<50" :model-value="(totals.totalFilesize/totals.fileSizeLimit)*100" />
	<Progress v-else-if="totals.fileSizeLimit>0 && (totals.totalFilesize/totals.fileSizeLimit)*100<75" :model-value="(totals.totalFilesize/totals.fileSizeLimit)*100" />
	<Progress v-else-if="totals.fileSizeLimit>0" :model-value="(totals.totalFilesize/totals.fileSizeLimit)*100" />
	</div>
	<div class="flex w-full flex-col">
	<div v-if="totals.transferLimit>0" class="text-primary font-small" style="font-weight: 500;">{{totals.transferLimit}} Files Published</div>
	<div class="text-primary font-small" v-else style="font-weight: 500;">Files Published</div>
	<div v-if="totals.transferLimit>0" class="d-flex text-foreground mb-1 font-small">{{totals.transferCount}} Files<span class="float-right"> {{ `${Math.min(100, Math.round((totals.transferCount / totals.transferLimit) * 100))}%` }} </span></div>
	<div v-else class="d-flex text-foreground mb-1 font-small">{{totals.transferCount}} Files</div>
	<div v-if="totals.transferLimit>0">
	<Progress height="0.35em" v-if="(totals.transferCount/totals.transferLimit)*100<50" :model-value="(totals.transferCount/totals.transferLimit)*100" variant="primary"/>
	<Progress height="0.35em" v-else-if="(totals.transferCount/totals.transferLimit)*100<75" :model-value="(totals.transferCount/totals.transferLimit)*100" variant="warning"/>
	<Progress height="0.35em" v-else :model-value="(totals.transferCount/totals.transferLimit)*100" variant="danger"/>
	</div>
	</div>
	<div class="flex w-full flex-col">
	<div class="text-primary font-small" v-if="totals.aiMinuteLimit > 0" style="font-weight: 500;">{{totals.aiMinuteLimit}} Minutes of AI Processing</div>
	<div class="text-primary font-small" v-else style="font-weight: 500;">Minutes of AI Processing</div>
	<div v-if="totals.aiMinuteLimit > 0" class="d-flex text-foreground mb-1 font-small">{{prettySeconds(totals.totalAISeconds)}}<span class="float-right">
{{ `${Math.min(100, Math.round((totals.totalAISeconds / (60*totals.aiMinuteLimit)) * 100))}%` }}
	</span></div>
	<div v-else class="d-flex text-muted mb-1 font-small">{{prettySeconds(totals.totalAISeconds)}}</div>
	<div v-if="totals.aiMinuteLimit>0">
	<Progress height="0.35em" v-if="(totals.totalAISeconds/(60*totals.aiMinuteLimit))*100<50" :model-value="(totals.totalAISeconds/(60*totals.aiMinuteLimit))*100" variant="primary"/>
	<Progress height="0.35em" v-else-if="(totals.totalAISeconds/(60*totals.aiMinuteLimit))*100<75" :model-value="(totals.totalAISeconds/(60*totals.aiMinuteLimit))*100" variant="warning"/>
	<Progress height="0.35em" v-else :model-value="(totals.totalAISeconds/(60*totals.aiMinuteLimit))*100" variant="danger"/>
	</div>
	</div>
	<div class="flex justify-center mt-4" v-if="billingPlan !== 'enterprise'">
		<Button class="w-full" @click="showUpgradePlans = true">VIEW PLANS</Button>
	</div>
	</template>
	<div v-else class="flex items-center justify-center py-8 text-sm text-muted-foreground">Loading...</div>
	</CardContent>
	</Card>
	</div>

	</div>
	</CardContent>
	</Card>

	<!-- Row 2: Current Plan (optional small) + Take a Tour (4/12) + Manage (8/12) — matches v6 -->
	<div class="grid grid-cols-1 md:grid-cols-12 gap-3">

	<!-- Current Plan — v6: col-lg-2, only shows for non-enterprise -->
	<div class="md:col-span-2" v-if="billingPlan !== 'enterprise'">
	<Card class="w-full h-full text-foreground">
	<CardHeader class="p-3 border-b"><CardTitle class="text-base font-normal">Current Plan</CardTitle></CardHeader>
	<CardContent class="p-3">
	<p class="text-xs text-muted-foreground mb-3">Choose the best fitting plan for your needs and have fun!</p>
	<div class="flex flex-col items-center justify-center py-2 gap-2">
		<h3 class="text-lg font-bold uppercase text-foreground">{{ billingPlan || 'free' }}</h3>
		<div class="text-xs text-muted-foreground" v-if="joinSince">User Since:</div>
		<div class="text-xs text-muted-foreground" v-if="joinSince">{{ joinSince }}</div>
		<Button size="sm" class="mt-2" v-if="billingPlan !== 'enterprise'" @click="showUpgradePlans = true">VIEW PLANS</Button>
	</div>
	</CardContent>
	</Card>
	</div>

	<!-- Take a Tour — v6: col-lg-4 -->
	<div :class="billingPlan !== 'enterprise' ? 'md:col-span-4' : 'md:col-span-4'">
	<Card class="w-full h-full text-foreground">
	<CardHeader class="p-3 border-b"><CardTitle class="text-base font-normal">Take a Tour</CardTitle></CardHeader>
	<CardContent class="p-3">
	<p class="text-xs text-muted-foreground mb-2">Here's a quick overview to get you started using the platform for your first project.</p>
	<div class="flex items-center justify-center">
	<video id="video-35181678" width="100%" height="auto"
	controls controlsList="nodownload"
	poster="https://storage.newspark.ca/storage/36988836/11">
	<source src="https://rstorage.newspark.ca/storage/36988836/2365" type="video/mp4">
	</video>
	</div>
	</CardContent>
	</Card>
	</div>

	<!-- Manage — v6: col-lg-8, contains Projects + Product Updates side by side -->
	<div :class="billingPlan !== 'enterprise' ? 'md:col-span-6' : 'md:col-span-8'">
	<Card class="w-full h-full text-foreground">
	<CardHeader class="p-3 border-b">
	<CardTitle class="text-base font-normal">Manage</CardTitle>
	<p class="text-xs text-muted-foreground">Unleash collaboration and operational efficiencies for your production and post-production teams.</p>
	</CardHeader>
	<CardContent class="p-3">
	<div class="grid grid-cols-1 md:grid-cols-2 gap-3">

	<!-- Projects Table -->
	<div>
	<div class="text-sm font-medium mb-2">Projects</div>
	<div v-if="projects.length > 0" class="overflow-x-auto max-h-[220px] overflow-y-auto">
		<Table>
		<TableHeader>
		<TableRow>
			<TableHead class="text-xs py-1">Name</TableHead>
			<TableHead class="text-xs py-1">Status</TableHead>
			<TableHead class="text-xs py-1">Days</TableHead>
		</TableRow>
		</TableHeader>
		<TableBody>
		<TableRow v-for="(project, index) in projects" :key="project.id">
			<TableCell class="text-xs text-muted-foreground py-1">{{ project.name }}</TableCell>
			<TableCell class="text-xs py-1">
				<span v-if="project.subStatus === 0" class="inline-block px-1.5 py-0.5 rounded text-white text-[10px] font-medium" style="background-color: #faab2a;">Pending</span>
				<span v-else-if="project.subStatus === 1" class="inline-block px-1.5 py-0.5 rounded text-white text-[10px] font-medium" style="background-color: #0d6efd;">In Progress</span>
				<span v-else-if="project.subStatus === 2" class="inline-block px-1.5 py-0.5 rounded text-white text-[10px] font-medium" style="background-color: #198754;">Completed</span>
				<span v-else-if="project.subStatus === 3" class="inline-block px-1.5 py-0.5 rounded text-white text-[10px] font-medium" style="background-color: #dc3545;">On Hold</span>
				<span v-else class="inline-block px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[10px] font-medium">Active</span>
			</TableCell>
			<TableCell class="text-xs text-muted-foreground py-1">{{ getProjectDays(project) }}</TableCell>
		</TableRow>
		</TableBody>
		</Table>
	</div>
	<div v-else class="flex flex-col items-center justify-center py-6 gap-2">
		<FolderPlus class="h-12 w-12 text-muted-foreground/30" />
		<div class="text-xs text-muted-foreground">No projects found</div>
		<Button variant="outline" size="sm" class="text-xs" @click.prevent="destUrl='https://mediamanager-docs.newspark.io/';support=1">Learn More</Button>
	</div>
	</div>

	<!-- Product Updates / System Status -->
	<div>
	<div class="flex justify-start space-x-3 mb-2">
	<div :class="{'border-b-2 border-primary': manageTabs=='product'}" @click.prevent="manageTabs='product'" class="cursor-pointer text-sm font-medium">Product Updates</div>
	<div :class="{'border-b-2 border-primary': manageTabs=='system'}" @click.prevent="manageTabs='system'" class="cursor-pointer text-sm font-medium">System Status</div>
	</div>
	<div v-if="manageTabs=='product'" class="flex justify-center items-center flex-col gap-2 py-4">
	<img src='https://assets.newspark.ca/2/v6/gfx/download.png' class="w-20 h-20"/>
	<div class="text-muted-foreground text-xs">No recent updates</div>
	</div>
	<div v-else>
	<div class="overflow-x-auto max-h-[220px] overflow-y-auto">
	<Table>
	<TableHeader>
	<TableRow>
	<TableHead class="text-xs py-1">Name</TableHead>
	<TableHead class="text-xs py-1">Current Status</TableHead>
	</TableRow>
	</TableHeader>
	<TableBody>
	<template v-for="(check,index) in pResults">
	<TableRow v-if='check.pronounceable_name.includes("North")' :key="index">
	<TableCell class="text-xs text-foreground py-1">{{ check.pronounceable_name }}</TableCell>
	<TableCell class="text-xs py-1">
	<CheckCircleIcon class="inline text-primary w-4 h-4" v-if="check.status=='up'"/>
	<span class="uppercase ml-1 text-xs">{{ check.status }}</span>
	</TableCell>
	</TableRow>
	</template>
	</TableBody>
	</Table>
	</div>
	<div class="mt-2"><a class="text-green-600 text-xs" style="text-decoration: none;font-weight:500" href="http://status.newspark.io/" target="_blank">View Full Status Report</a></div>
	</div>
	</div>

	</div>
	</CardContent>
	</Card>
	</div>

	</div>

	</div>

	<!-- Upgrade Plans Sheet (reusable, slides from top) -->
	<UpgradePlans v-model="showUpgradePlans" />

	<!-- Learn More / Support Sheet -->
	<Sheet :open="support===1" @update:open="(val: boolean) => { if(!val) support=0 }">
	<SheetContent side="right" class="w-full sm:max-w-xl md:max-w-2xl overflow-hidden">
	<SheetHeader>
	<SheetTitle>Documentation</SheetTitle>
	<SheetDescription>Learn more about this feature</SheetDescription>
	</SheetHeader>
	<div class="flex-1 h-[calc(100vh-120px)] mt-4">
	<iframe v-if="support===1 && destUrl" :src="destUrl" class="w-full h-full border-0 rounded" allow="fullscreen" />
	</div>
	</SheetContent>
	</Sheet>

	</div>
	<div v-else class="flex-1 space-y-4 p-3 md:p-4 z-10">
	<Tabs default-value="overview" class="space-y-4" v-model="selectedTab">
	<div class="flex items-center justify-between gap-2">
	<!-- Scrollable tabs on mobile -->
	<div class="overflow-x-auto scrollbar-hide flex-1 min-w-0">
	<TabsList class="inline-flex w-auto min-w-max">
	<TabsTrigger value="overview" class="text-xs md:text-sm px-2 md:px-3">Overview</TabsTrigger>
	<TabsTrigger value="mydashboard" class="text-xs md:text-sm px-2 md:px-3">My Dashboard</TabsTrigger>
	<TabsTrigger value="transfers" class="text-xs md:text-sm px-2 md:px-3">Transfers</TabsTrigger>
	<TabsTrigger value="conversions" class="text-xs md:text-sm px-2 md:px-3">Conversions</TabsTrigger>
	<TabsTrigger value="activity" class="text-xs md:text-sm px-2 md:px-3">Activity</TabsTrigger>
	</TabsList>
	</div>
	<div v-if="selectedTab=='mydashboard'" class="shrink-0">
	<Sheet>
	<SheetTrigger><TuneIcon class="w-6 h-6 md:w-8 md:h-8 text-primary"/></SheetTrigger>
	<SheetContent style="max-width: 80vw">
	<SheetHeader>
	<SheetTitle class="text-subtitle">Customize Your Dashboard</SheetTitle>
	<SheetDescription>
	<ChartChooser/>
	</SheetDescription>
	</SheetHeader>
	</SheetContent>
	</Sheet>
	</div>
	</div>
	<TabsContent value="overview" class="space-y-4">
	<!-- Summary Cards: 2-col on mobile, 4-col on lg -->
	<div class="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
	<Card>
	<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-6">
	<CardTitle class="text-xs md:text-subtitle font-medium">Uploads</CardTitle>
	<InboxArrowDownIcon class="h-4 w-4 md:h-5 md:w-5 text-muted-foreground"/>
	</CardHeader>
	<CardContent class="p-3 md:p-6 pt-0">
	<div class="text-lg md:text-title font-bold" v-if="summary">
	{{ formatNumber(summary.FileCounts.Total) }}
	</div>
	<p class="text-[10px] md:text-xs text-muted-foreground">Last 30 days</p>
	</CardContent>
	</Card>
	<Card>
	<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-6">
	<CardTitle class="text-xs md:text-sm font-medium">Members</CardTitle>
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" class="h-4 w-4 text-muted-foreground"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
	</CardHeader>
	<CardContent class="p-3 md:p-6 pt-0">
	<div class="text-lg md:text-title font-bold" v-if="userSummary">+{{ userSummary.NewUsers }}</div>
	<p class="text-[10px] md:text-xs text-muted-foreground">New users last 30 days</p>
	</CardContent>
	</Card>
	<Card>
	<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-6">
	<CardTitle class="text-xs md:text-sm font-medium">Views</CardTitle>
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" class="h-4 w-4 text-muted-foreground"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>
	</CardHeader>
	<CardContent class="p-3 md:p-6 pt-0">
	<div class="text-lg md:text-title font-bold" v-if="summary">{{ formatNumber(summary.Views.Total) }}</div>
	<p class="text-[10px] md:text-xs text-muted-foreground">Last 30 days</p>
	</CardContent>
	</Card>
	<Card>
	<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-6">
	<CardTitle class="text-xs md:text-sm font-medium">Transfers</CardTitle>
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" class="h-4 w-4 text-muted-foreground"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>
	</CardHeader>
	<CardContent class="p-3 md:p-6 pt-0">
	<div class="text-lg md:text-title font-bold" v-if="summary">+{{ formatNumber(summary.Transfers) }}</div>
	<p class="text-[10px] md:text-xs text-muted-foreground">Published items</p>
	</CardContent>
	</Card>
	</div>

	<!-- Row 1: Uploads chart + Recent Uploads — stack on mobile -->
	<div class="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-7">
	<Card class="lg:col-span-4">
	<CardHeader class="p-3 md:p-6">
	<CardTitle class="text-subtitle border-b">Uploads by Day</CardTitle>
	</CardHeader>
	<CardContent class="p-2 md:pl-2">
	<BarChart :data="series" :height="'300px'"/>
	</CardContent>
	</Card>
	<Card class="lg:col-span-3 w-full">
	<CardHeader class="p-3 md:p-6">
	<CardTitle class="text-subtitle border-b">Recent Uploads</CardTitle>
	<CardDescription>
	<div>{{ totalMedia }} uploads in the last 30 days.</div>
	</CardDescription>
	</CardHeader>
	<CardContent class="p-3 md:p-6">
	<div class="max-h-[300px] md:max-h-[350px] overflow-y-auto">
	<MediaList v-model="selectedMedia" v-if="vhostId !== null" :filters="filters" type="list" :vhostId="vhostId" sort-order="upload desc" default-profile="11" @set-total-count="setTotalMedia"/>
	</div>
	</CardContent>
	</Card>
	</div>

	<!-- Row 2: Upload Time + Conversion Time — already stacks with md:grid-cols-2 -->
	<div class="grid gap-3 md:gap-4 grid-cols-1 md:grid-cols-2">
	<Card>
	<CardHeader class="p-3 md:p-6">
	<CardTitle class="text-subtitle border-b">Upload Time</CardTitle>
	<CardDescription class="text-xs md:text-sm">Average upload processing time per day</CardDescription>
	</CardHeader>
	<CardContent class="p-2 md:pl-2 h-[250px] md:h-[300px]">
	<BarChart :data="uploadTimeSeries" :height="'220px'"/>
	</CardContent>
	</Card>
	<Card>
	<CardHeader class="p-3 md:p-6">
	<CardTitle class="text-subtitle border-b">Conversion Time</CardTitle>
	<CardDescription class="text-xs md:text-sm">Average media conversion time per day</CardDescription>
	</CardHeader>
	<CardContent class="p-2 md:pl-2 h-[250px] md:h-[300px]">
	<BarChart :data="conversionTimeSeries" :height="'220px'"/>
	</CardContent>
	</Card>
	</div>

	<!-- Row 3: File type breakdown — 2-col on mobile, 4-col on lg -->
	<div class="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
	<Card>
	<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-6">
	<CardTitle class="text-xs md:text-sm font-medium">Images</CardTitle>
	</CardHeader>
	<CardContent class="p-3 md:p-6 pt-0">
	<div class="text-xl md:text-2xl font-bold" v-if="summary">{{ formatNumber(summary.FileCounts.Image) }}</div>
	<p class="text-[10px] md:text-xs text-muted-foreground">{{ summary ? Math.round((summary.FileCounts.Image / Math.max(1, summary.FileCounts.Total)) * 100) : 0 }}% of total</p>
	</CardContent>
	</Card>
	<Card>
	<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-6">
	<CardTitle class="text-xs md:text-sm font-medium">Videos</CardTitle>
	</CardHeader>
	<CardContent class="p-3 md:p-6 pt-0">
	<div class="text-xl md:text-2xl font-bold" v-if="summary">{{ formatNumber(summary.FileCounts.Video) }}</div>
	<p class="text-[10px] md:text-xs text-muted-foreground">{{ summary ? Math.round((summary.FileCounts.Video / Math.max(1, summary.FileCounts.Total)) * 100) : 0 }}% of total</p>
	</CardContent>
	</Card>
	<Card>
	<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-6">
	<CardTitle class="text-xs md:text-sm font-medium">Audio</CardTitle>
	</CardHeader>
	<CardContent class="p-3 md:p-6 pt-0">
	<div class="text-xl md:text-2xl font-bold" v-if="summary">{{ formatNumber(summary.FileCounts.Audio) }}</div>
	<p class="text-[10px] md:text-xs text-muted-foreground">{{ summary ? Math.round((summary.FileCounts.Audio / Math.max(1, summary.FileCounts.Total)) * 100) : 0 }}% of total</p>
	</CardContent>
	</Card>
	<Card>
	<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-6">
	<CardTitle class="text-xs md:text-sm font-medium">Documents</CardTitle>
	</CardHeader>
	<CardContent class="p-3 md:p-6 pt-0">
	<div class="text-xl md:text-2xl font-bold" v-if="summary">{{ formatNumber(summary.FileCounts.Text) }}</div>
	<p class="text-[10px] md:text-xs text-muted-foreground">{{ summary ? Math.round((summary.FileCounts.Text / Math.max(1, summary.FileCounts.Total)) * 100) : 0 }}% of total</p>
	</CardContent>
	</Card>
	</div>
	</TabsContent>
	<TabsContent value="transfers" class="space-y-4">
	<TransferList/>
	</TabsContent>
	<TabsContent value="mydashboard" class="space-y-4">
	<!-- On mobile: simple stacked cards. On desktop: grid layout -->
	<div class="hidden md:block">
	<GridLayout v-model:layout="layout" :col-num="6" :row-height="220" :is-draggable="false" :is-resizable="true" :margin="[16, 16]" :vertical-compact="false" :use-css-transforms="true" class="flex-1 p-4" :auto-size="true">
	<GridItem v-for="item in layout" :key="item.i" :x="item.x" :y="item.y" :w="item.w" :h="item.h" :i="item.i" class="bg-none p-3 relative">
	<div class="w-full h-full">
	<component :is="getComponent(item.type)" :data="item.data" size="medium" :height="item.h" />
	</div>
	</GridItem>
	</GridLayout>
	</div>
	<!-- Mobile: simple stacked view -->
	<div class="md:hidden space-y-3">
	<Card v-for="item in layout" :key="item.i" class="w-full">
	<CardContent class="p-3">
	<component :is="getComponent(item.type)" :data="item.data" size="small" :height="1" />
	</CardContent>
	</Card>
	</div>
	</TabsContent>
	</Tabs>
	</div>
</template>
